/**
 * TwinDashboardCard.jsx — iter830 (Feb 2026)
 *
 * "🧬 Моите 20 близнака" — dysregulation gauge per twin.
 *
 * Each twin shows a progress bar (0-100%), band chip (Optimal /
 * Borderline / Dysregulated / Severely Dysregulated), the data that
 * drives it, and the top-3 KEGG pathways it modulates.
 *
 * Sits after PathwayExplorer in the BHI page so the user sees the
 * biological reason WHY certain pathways are boosted.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Users, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "@/App";
import { useHealthEvent } from "@/utils/healthEvents";

const BAND_STYLE = {
  optimal:               { label: "Оптимално",         cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300", bar: "bg-emerald-500" },
  borderline:            { label: "На границата",      cls: "bg-amber-100    text-amber-700    dark:bg-amber-500/20    dark:text-amber-300",    bar: "bg-amber-500" },
  dysregulated:          { label: "Дисрегулиран",      cls: "bg-orange-100   text-orange-700   dark:bg-orange-500/20   dark:text-orange-300",   bar: "bg-orange-500" },
  severely_dysregulated: { label: "Силно дисрегулиран", cls: "bg-rose-100    text-rose-700     dark:bg-rose-500/20     dark:text-rose-300",     bar: "bg-rose-500" },
};

export default function TwinDashboardCard({ isDark = false }) {
  const { API, user } = useAuth();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/bhi/twin-dashboard`);
      setData(data);
    } catch { setData({ twins: [], active_count: 0, dysregulated_count: 0 }); }
  }, [API, user]);

  useEffect(() => { load(); }, [load]);
  useHealthEvent("conditions-changed", () => { load(); });

  if (!user || !data) return null;

  const visible = showInactive ? data.twins : data.twins.filter((t) => t.active);
  const cardCls = `rounded-2xl border ${isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200"}`;

  return (
    <section className={`${cardCls} p-5 mb-4`} data-testid="twin-dashboard-card">
      <header className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
          <Users className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-extrabold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
            🧬 Моите 20 близнака ({data.active_count} активни, {data.dysregulated_count} дисрегулирани)
          </h3>
          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            Всеки &laquo;близнак&raquo; отразява биологичен домейн. Дисрегулираните близнаци усилват свързаните KEGG пътища с до +10% на BDI картите.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowInactive((v) => !v)}
          className="text-[10px] px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-700 dark:text-slate-200 hover:opacity-80"
          data-testid="twin-toggle-inactive"
        >
          {showInactive ? "Скрий неактивни" : `Покажи всички (18)`}
        </button>
      </header>

      <div className="space-y-1.5" data-testid="twin-dashboard-list">
        {visible.length === 0 && (
          <div className={`p-4 rounded-lg text-sm ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-700"}`}>
            Няма активни близнаци — добавете labs, diagnoses или meal log за да оживят.
          </div>
        )}
        {visible.map((t) => (
          <TwinRow
            key={t.twin_id}
            t={t}
            isOpen={expanded === t.twin_id}
            onToggle={() => setExpanded(expanded === t.twin_id ? null : t.twin_id)}
            isDark={isDark}
          />
        ))}
      </div>
      <p className={`mt-3 text-[10px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>
        {data.disclaimer}
      </p>
    </section>
  );
}

function TwinRow({ t, isOpen, onToggle, isDark }) {
  const s = BAND_STYLE[t.band] || BAND_STYLE.optimal;
  const rowCls = `rounded-lg border ${isDark ? "border-slate-700 bg-slate-900/40" : "border-slate-200 bg-white"}`;
  return (
    <div className={rowCls} data-testid={`twin-row-${t.twin_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-center gap-2 p-2.5"
      >
        {isOpen ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />}
        <span className="text-lg shrink-0">{t.icon}</span>
        <span className="text-sm font-semibold flex-1 min-w-0 truncate">{t.name_bg}</span>
        <div className="w-24 md:w-40">
          <div className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div
              className={`h-full ${s.bar} transition-all`}
              style={{ width: `${t.state_pct}%` }}
              data-testid={`twin-bar-${t.twin_id}`}
            />
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${s.cls}`}>
          {t.state_pct}%
        </span>
      </button>
      {isOpen && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
          <div className="text-[11px] opacity-80">
            <b>Данни:</b> {t.why}
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest mb-1 opacity-60">
              Модулирани KEGG пътища
            </div>
            <div className="flex flex-wrap gap-1">
              {t.top_pathways.map((p, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300"
                  title={`Чувствителност: ${p.sensitivity.toFixed(2)}`}
                >
                  🧬 {p.pathway}
                </span>
              ))}
            </div>
          </div>
          <div className={`text-[10px] font-bold uppercase tracking-widest mt-1 inline-flex px-2 py-0.5 rounded-full ${s.cls}`}>
            {s.label}
          </div>
        </div>
      )}
    </div>
  );
}
